import React, { useEffect, useState } from "react";
import DataTable, { defaultThemes } from "react-data-table-component";
import { get, post } from "../../services/api";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import Select from "react-select";
import { Link, useNavigate } from "react-router-dom";
import { customTableStyles } from "../../assets/CustomTableStyles";
import PayChallanModal from "./PayChallanModal";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function AdmissionList() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const navigate = useNavigate();

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
  } = useForm();

  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [centersData, setCentersData] = useState([]);
  const [admissionsData, setAdmissionsData] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentAdmissionRowData, setCurrentAdmissionRowData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setCentersData(response);

        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );
          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCenters();
  }, []);

  const fetchAdmissionsList = async (centerId) => {
    try {
      const response = await get(
        `Registration/GetAllRegistrationByOrganizationAndCenterIdAsync?organizationId=${organizationId}&centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setAdmissionsData(response?.registration);
    } catch (error) {
      console.error("Error fetching registration:", error);
    }
  };

  useEffect(() => {
    if (centerId > 0) {
      fetchAdmissionsList(centerId);
    }
  }, [centerId, reset]);

  const columns = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      omit: true,
      width: "70px",
    },
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      width: "60px",
    },
    {
      name: "Full Name",
      selector: (row) => `${row.firstName} ${row.lastName}`,
      cell: (row) => {
        return (
          <div className="d-flex align-items-center gap-2">
            <div className="">{row.firstName + " " + row.lastName}</div>
          </div>
        );
      },
      sortable: true,
    },
    {
      name: "Registration No.",
      selector: (row) => row.registrationNo,
    },
    {
      name: "Guardian's Identity No.",
      selector: (row) => row.guardianIdentityNo,
    },
    {
      name: "Guardian's Mobile No.",
      selector: (row) => row.mobileNo,
    },
    {
      name: "Challan Paid",
      selector: (row) => (row.isPaid === 1 ? "Yes" : "No"),
      sortable: true,
    },
    {
      name: "Action",
      selector: (row) => {
        return (
          <div className="d-flex gap-1">
            {row.isPaid === 0 && (
              <button
                type="button"
                className="btn btn-outline-primary py-1 px-2"
                onClick={() => handlePayChallanClick(row)}
              >
                Pay Challan
              </button>
            )}

            {row.isPaid === 1 && (
              <button
                type="button"
                className="btn btn-primary py-1 px-2"
                onClick={() => handleCreateStudentClick(row.voucherNo)}
              >
                Create Student
              </button>
            )}

            {row.isPaid === -1 && (
              <button
                type="button"
                className="btn btn-outline-primary py-1 px-2"
                onClick={() =>
                  handleCompleteAssessmentClick(row.registrationNo)
                }
              >
                New Admission
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const exportToExcel = () => {
    const filteredData = admissionsData.map((row, index) => ({
      "Sr No": index + 1,
      "Full Name": `${row.firstName} ${row.lastName}`,
      "Registration No": row.registrationNo,
      "Guardian ID No": row.guardianIdentityNo,
      "Guardian Mobile No": row.mobileNo,
      "Challan Paid": row.isPaid === 1 ? "Yes" : "No",
      // Action: row.Action, // Assuming you have an 'Action' property, but replace it with the actual key for the 'Action' column in your data
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Admissions");
    XLSX.writeFile(workbook, "Admissions.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "All Admissions";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const excludedColumns = ["Action", "ID"]; // Now excludes both 'Action' and 'ID'
    const includedColumns = columns.filter(
      (column) => !excludedColumns.includes(column.name)
    );

    const bodyData = admissionsData.map((row, index) => {
      return [
        (currentPage - 1) * rowsPerPage + index + 1,
        ...includedColumns
          .filter((col) => col.name !== "Sr No.")
          .map((column) => column.selector(row)),
      ];
    });

    doc.autoTable({
      head: [
        [
          "Sr No.",
          ...includedColumns
            .filter((col) => col.name !== "Sr No.")
            .map((col) => col.name),
        ],
      ],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("Admissions.pdf");
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  const handlePayChallanClick = async (row) => {
    try {
      const response = await get(
        `AssessmentType/GetAdmissionVoucherByRegistartionNoAsync?registartionNo=${row.registrationNo}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        setModalData(response?.admissionVoucher[0]);
        setShowModal(true);
        setCurrentAdmissionRowData(row);
      } else {
        console.error("Error fetching fee details");
      }
    } catch (error) {
      console.error("Error fetching admission voucher:", error);
    }
  };

  const handleCompleteAssessmentClick = async (registartionNo) => {
    navigate(`/new-admission/${registartionNo}`);
  };

  const handleCreateStudentClick = async (registartionNo) => {
    navigate(`/student-form?voucherNo=${registartionNo}`);
  };

  const submitPayChallan = async (formData) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const currentDate = new Date();
    const dateString = currentDate.toISOString();

    const formDataWithPaymentInfo = {
      ...formData,
      createdBy: localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
      paidBy: localStorage.getItem("userId"),
      isPaid: true,
      paymentDate: dateString,
      id: formData.id,
      voucherNo: formData.voucherNo,
    };

    try {
      const response = await post(
        `AssessmentType/UpdateAdmissionVoucherPaidAsync`,
        formDataWithPaymentInfo,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setShowModal(false);
        await fetchAdmissionsList(centerId);
      }
    } catch (error) {
      console.error("Error saving category:", error);
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Admissions</h2>
        <div className="ms-auto">
          <div className="d-flex gap-2">
            <a
              className="btn btn-primary btn-sm"
              href="/student-first-registration"
              target="_blank"
            >
              Registration <i className="fa fa-external-link"></i>
            </a>
            {/* <Link
              to="/new-admission"
              className="btn btn-outline-primary btn-sm"
            >
              New Admission
            </Link> */}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div
            className="d-flex align-items-center mb-3"
            style={{ marginTop: 0 }}
          >
            <div style={{ width: "220px" }}>
              <label className="form-label">Center</label>
              <Controller
                name="centerId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={centersData.map((center) => ({
                      value: center.id,
                      label: center.name,
                    }))}
                    isDisabled={savedCenterId !== "0"}
                    onChange={(selectedOption, actionMeta) => {
                      if (actionMeta.action === "clear") {
                        setValue(`centerId`, null);
                      } else {
                        setValue(
                          `centerId`,
                          selectedOption || { value: 0, label: "" }
                        );
                        setCenterId(selectedOption.value);
                      }
                      trigger(`centerId`);
                    }}
                    className="react-select-style"
                    classNamePrefix="react-select-style-pref"
                    // isClearable={!savedCenterId}
                  />
                )}
              />
              {errors.centerId && (
                <small className="text-danger">{errors.centerId.message}</small>
              )}
            </div>

            <div className="ms-auto" style={{ marginTop: 0 }}>
              <div className="d-flex align-items-start gap-1">
                <button
                  type="button"
                  data-bs-toggle="tooltip"
                  data-bs-custom-class="custom-tooltip"
                  title="Export to Excel"
                  className="btn btn-outline-success px-2 py-1"
                  onClick={exportToExcel}
                >
                  <i className="fs-8 fa fa-file-excel"></i>
                </button>
                <button
                  type="button"
                  data-bs-toggle="tooltip"
                  data-bs-custom-class="custom-tooltip"
                  title="Export to PDF"
                  className="btn btn-outline-danger px-2 py-1"
                  onClick={exportToPDF}
                >
                  <i className="fs-8 fa fa-file-pdf"></i>
                </button>
              </div>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={admissionsData}
            pagination
            dense
            customStyles={customTableStyles}
            // onRowClicked={handleRowClick}
            // highlightOnHover={true}
            // pointerOnHover={true}
          />
        </div>
      </div>

      {showModal && (
        <PayChallanModal
          title="Pay Challan"
          onClose={() => setShowModal(false)}
          data={modalData}
          payChallan={submitPayChallan}
        />
      )}
    </>
  );
}

export default AdmissionList;
